import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { LOCATION_CACHE_VERSION } from "@/lib/browseLocation";

const ASKED_KEY = "referlinc_location_asked";
const CACHE_KEY = "referlinc_location_cache";

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.v !== LOCATION_CACHE_VERSION) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    if (parsed?.lat == null || parsed?.lng == null) return null;
    return { lat: Number(parsed.lat), lng: Number(parsed.lng), source: parsed.source || "cache" };
  } catch {
    return null;
  }
}

function writeCache(lat, lng, source) {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ v: LOCATION_CACHE_VERSION, lat, lng, source, at: Date.now() }),
    );
  } catch {
    /* ignore */
  }
}

/**
 * Browse-location resolver for landing / referrer home.
 * Fallback chain: GPS → stored profile location → city/ZIP area → none (generosity-only).
 * Never blocks browsing. Does not run on /r/{code}.
 */
export function useBrowseLocation({ enabled = true } = {}) {
  const { user, ready, refresh } = useAuth();
  const [coords, setCoords] = useState(() => readCache());
  const [status, setStatus] = useState("idle"); // idle | prompting | granted | denied | area | none
  const [areaInput, setAreaInput] = useState("");
  const [areaError, setAreaError] = useState("");
  const [savingArea, setSavingArea] = useState(false);
  const askedRef = useRef(false);

  const apply = useCallback((lat, lng, source) => {
    if (lat == null || lng == null) return;
    setCoords({ lat, lng, source });
    writeCache(lat, lng, source);
    setStatus(source === "area" ? "area" : "granted");
  }, []);

  const persistGps = useCallback(async (lat, lng) => {
    writeCache(lat, lng, "gps");
    if (!user) return;
    try {
      await api.patch("/me/location", { latitude: lat, longitude: lng });
      refresh?.();
    } catch {
      /* offline / unauthed path still works via cache */
    }
  }, [user, refresh]);

  useEffect(() => {
    if (!enabled || !ready || askedRef.current) return;
    askedRef.current = true;

    const profile = user?.location;
    // Prefer server profile (may have been re-geocoded) over any leftover cache.
    if (profile?.latitude != null && profile?.longitude != null) {
      apply(profile.latitude, profile.longitude, profile.source || "profile");
    }

    const alreadyAsked = localStorage.getItem(ASKED_KEY) === "1";
    if (!navigator.geolocation) {
      setStatus((s) => (coords || profile ? s : "denied"));
      return;
    }

    if (alreadyAsked && !coords && !(profile?.latitude != null)) {
      setStatus("denied");
      return;
    }

    if (alreadyAsked && (coords || profile?.latitude != null)) {
      return;
    }

    setStatus("prompting");
    localStorage.setItem(ASKED_KEY, "1");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        apply(lat, lng, "gps");
        persistGps(lat, lng);
      },
      () => {
        if (profile?.latitude != null) {
          apply(profile.latitude, profile.longitude, profile.source || "profile");
        } else if (coords) {
          setStatus(coords.source === "area" ? "area" : "granted");
        } else {
          setStatus("denied");
        }
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 5 * 60 * 1000 },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ready, user?.id]);

  // When auth loads a profile after cache init, refresh from server (fixes stale ZIP coords).
  useEffect(() => {
    if (!ready || !user?.location) return;
    const loc = user.location;
    if (loc.latitude == null || loc.longitude == null) return;
    if (loc.source === "area" || loc.geocode_version !== LOCATION_CACHE_VERSION) {
      apply(loc.latitude, loc.longitude, loc.source || "profile");
    }
  }, [ready, user?.location?.latitude, user?.location?.longitude, user?.location?.geocode_version, apply]);

  const setArea = async (raw) => {
    const area = (raw || areaInput || "").trim();
    if (!area) {
      setAreaError("Enter a city or ZIP");
      return false;
    }
    setSavingArea(true);
    setAreaError("");
    try {
      if (user) {
        const { data } = await api.patch("/me/location", { area });
        const loc = data.location;
        apply(loc.latitude, loc.longitude, "area");
        refresh?.();
      } else {
        const { data } = await api.get("/geocode", { params: { q: area } });
        apply(data.latitude, data.longitude, "area");
        writeCache(data.latitude, data.longitude, "area");
      }
      return true;
    } catch (e) {
      setAreaError(e?.response?.data?.detail || "Could not find that city or ZIP");
      return false;
    } finally {
      setSavingArea(false);
    }
  };

  return {
    coords,
    status,
    searchParams: coords ? { lat: coords.lat, lng: coords.lng } : {},
    areaInput,
    setAreaInput,
    areaError,
    savingArea,
    setArea,
    needsAreaFallback: status === "denied" && !coords,
  };
}
